import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, User, X } from 'lucide-react';
import { Database } from '../../../lib/database.types';
import toast from 'react-hot-toast';

type Customer = Database['public']['Tables']['customers']['Row'];

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: Partial<Customer>) => Promise<void>;
  onCancel: () => void;
}

export function CustomerForm({ customer, onSubmit, onCancel }: CustomerFormProps) {
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    phone: customer?.phone || '',
    email: customer?.email || ''
  });

  // ודא שהערכים של formData מתעדכנים כאשר customer משתנה (לעריכה מהירה)
  React.useEffect(() => {
    setFormData({
      name: customer?.name || '',
      phone: customer?.phone || '',
      email: customer?.email || ''
    });
  }, [customer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate phone number
    if (!/^05\d{8}$/.test(formData.phone)) {
      toast.error('מספר טלפון לא תקין');
      return;
    }

    // Validate email if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('כתובת אימייל לא תקינה');
      return;
    }

    try {
      await onSubmit(formData);
      onCancel();
    } catch (error) {

    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="bg-white p-5 rounded-3xl shadow-2xl space-y-5 max-w-md w-full mx-4"
      onSubmit={handleSubmit}
    >
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          {customer ? 'עריכת לקוח' : 'לקוח חדש'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 bg-gray-100 rounded-full text-gray-500 hover:bg-gray-200 active:bg-gray-300 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">
            שם מלא
          </label>
          <div className="relative">
            <User className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full pr-10 pl-3 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
              required
              placeholder="שם מלא"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">
            טלפון
          </label>
          <div className="relative">
            <Phone className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full pr-10 pl-3 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
              placeholder="0501234567"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">
            אימייל <span className="text-gray-400">(אופציונלי)</span>
          </label>
          <div className="relative">
            <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full pr-10 pl-3 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
              placeholder="example@email.com"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 transition-colors"
        >
          ביטול
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 text-sm font-medium bg-blue-500 text-white rounded-xl hover:bg-blue-600 active:bg-blue-700 transition-colors"
        >
          {customer ? 'שמור' : 'הוסף'}
        </button>
      </div>
    </motion.form>
  );
}